import { firestorePremiumCovers } from '@lib/firebase'
import { CClient } from '@common/utils'

/**
 * Generate cover path from category and number
 */
const generateCoverPath = (categoryId: string, coverNumber: number): string => {
  return `/images/student-cover-images/${categoryId}/cover_image_${coverNumber}.jpg`
}

/**
 * Generate thumbnail path from full cover path
 * Used for grid display in modal to improve performance
 */
export const generateThumbnailPath = (coverPath: string): string => {
  const parts = coverPath.split('/')
  const filename = parts[parts.length - 1]
  const thumbnailFilename = `thumb_${filename}`
  parts[parts.length - 1] = thumbnailFilename
  return parts.join('/')
}

/**
 * Extract cover number from path
 * e.g., "/images/student-cover-images/nature/cover_image_1.jpg" -> 1
 */
const extractCoverNumber = (coverPath: string): number | null => {
  const match = coverPath.match(/cover_image_(\d+)\.jpg/)
  return match ? parseInt(match[1], 10) : null
}

/**
 * Unlock a cover image for a student
 * Calls the backend API which:
 * 1. Deducts points from student account
 * 2. Records unlock in StudentPointLog (source of truth)
 * 3. Returns remaining points
 */
export const unlockCover = async ({
  categoryId,
  coverNumber,
  pointsCost,
}: {
  categoryId: string
  coverNumber: number
  pointsCost: number
}): Promise<{
  success: boolean
  remainingPoints?: number
  error?: string
}> => {
  try {
    // Step 1: Call backend API to deduct points
    const response = await CClient('/api/v1/unlock-cover/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        category_id: categoryId,
        cover_number: coverNumber,
        points_cost: pointsCost,
      }),
    })

    const data = await response.json()

    if (!response.ok) {
      return {
        success: false,
        error: data.error || 'Failed to unlock cover',
      }
    }

    // Backend API is the source of truth - unlock is recorded in StudentPointLog
    return {
      success: true,
      remainingPoints: data.remainingPoints,
    }
  } catch (error) {
    return {
      success: false,
      error: 'An unexpected error occurred',
    }
  }
}

/**
 * Fetch all unlocked covers for a user from Django API
 * Returns full cover paths reconstructed from categoryId and coverNumber
 */
export const fetchUnlockedCovers = async (
  _userId: string
): Promise<string[]> => {
  try {
    const response = await CClient('/api/v1/unlocked-covers/', {
      method: 'GET',
    })

    if (!response.ok) {
      return []
    }

    const data = await response.json()

    // Convert {categoryId, coverNumber} objects to full cover paths
    const covers = data.unlockedCovers.map(
      (cover: { categoryId: string; coverNumber: number }) =>
        generateCoverPath(cover.categoryId, cover.coverNumber)
    )

    return covers
  } catch (error) {
    return []
  }
}

/**
 * Save the selected cover for a user
 */
export const saveSelectedCover = async (
  userId: string,
  categoryId: string,
  coverNumber: number
): Promise<boolean> => {
  try {
    if (!firestorePremiumCovers) {
      return false
    }

    await firestorePremiumCovers
      .collection('user_profile_covers')
      .doc(userId)
      .set({
        selectedCategoryId: categoryId,
        selectedCoverNumber: coverNumber,
        updatedAt: new Date(),
      })

    return true
  } catch (error) {
    return false
  }
}

/**
 * Load the selected cover for a user
 * Returns the full cover path or null if no cover is selected
 */
export const loadSelectedCover = async (
  userId: string
): Promise<string | null> => {
  try {
    if (!firestorePremiumCovers) {
      return null
    }

    const doc = await firestorePremiumCovers
      .collection('user_profile_covers')
      .doc(userId)
      .get()

    if (!doc.exists) {
      return null
    }

    const data = doc.data()
    if (!data || !data.selectedCategoryId || !data.selectedCoverNumber) {
      return null
    }

    return generateCoverPath(data.selectedCategoryId, data.selectedCoverNumber)
  } catch (error) {
    return null
  }
}

/**
 * Save the cover position for a user
 */
export const saveCoverPosition = async (
  userId: string,
  position: { x: number; y: number }
): Promise<boolean> => {
  try {
    if (!firestorePremiumCovers) {
      return false
    }

    await firestorePremiumCovers
      .collection('user_profile_covers')
      .doc(userId)
      .update({
        position,
        updatedAt: new Date(),
      })

    return true
  } catch (error) {
    return false
  }
}

/**
 * Load the cover position for a user
 */
export const loadCoverPosition = async (
  userId: string
): Promise<{ x: number; y: number } | null> => {
  try {
    if (!firestorePremiumCovers) {
      return null
    }

    const doc = await firestorePremiumCovers
      .collection('user_profile_covers')
      .doc(userId)
      .get()

    if (!doc.exists) {
      return null
    }

    const data = doc.data()
    return data?.position || null
  } catch (error) {
    return null
  }
}

export { generateCoverPath, extractCoverNumber }
